import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { 
  ChartBar, 
  Calendar,
  Warning,
  CurrencyDollar,
  ArrowClockwise,
  FileText,
  Users,
  TrendUp,
  Clock,
  Download
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ARAgingReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBucket, setSelectedBucket] = useState(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get('/billing/reports/ar-aging');
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load A/R aging report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      insurance_paid: 'bg-teal-100 text-teal-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status?.replace('_', ' ').charAt(0).toUpperCase() + status?.slice(1).replace('_', ' ') || 'Pending'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <ArrowClockwise size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  const buckets = report?.aging_buckets || {};

  return (
    <div className="p-8" data-testid="ar-aging-report-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            A/R Aging Report
          </h1>
          <p className="text-slate-500">
            Accounts receivable aging analysis • {formatDate(report?.report_date)}
          </p>
        </div>
        <button
          onClick={fetchReport}
          className="btn-outline flex items-center gap-2"
        >
          <ArrowClockwise size={18} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-teal-100 rounded-lg">
              <CurrencyDollar size={24} className="text-teal-600" />
            </div>
            <span className="text-sm text-slate-500">Total A/R</span>
          </div>
          <p className="text-3xl font-bold text-slate-900">
            {formatCurrency(report?.summary?.total_ar)}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {report?.summary?.total_invoices} invoices
          </p>
        </div>
        
        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendUp size={24} className="text-green-600" />
            </div>
            <span className="text-sm text-slate-500">Current (0-30)</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {formatCurrency(report?.summary?.current_0_30)}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {report?.summary?.percentage_current}% of total
          </p>
        </div>
        
        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={24} className="text-amber-600" />
            </div>
            <span className="text-sm text-slate-500">31-90 Days</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">
            {formatCurrency((report?.summary?.days_31_60 || 0) + (report?.summary?.days_61_90 || 0))}
          </p>
        </div>
        
        <div className="card-clinical p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <Warning size={24} className="text-red-600" />
            </div>
            <span className="text-sm text-slate-500">Over 90 Days</span>
          </div>
          <p className="text-3xl font-bold text-red-600">
            {formatCurrency(report?.summary?.over_90)}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {report?.summary?.percentage_over_90}% of total
          </p>
        </div>
      </div>

      {/* Aging Buckets Chart/Grid */}
      <div className="card-clinical p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Aging Breakdown</h2>
        
        {/* Visual Progress Bar */}
        <div className="h-8 rounded-lg overflow-hidden flex mb-4">
          {report?.summary?.total_ar > 0 && (
            <>
              <div 
                className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${report?.summary?.percentage_current || 0}%` }}
              >
                {report?.summary?.percentage_current > 10 ? '0-30' : ''}
              </div>
              <div 
                className="bg-yellow-500 flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${((report?.summary?.days_31_60 || 0) / report?.summary?.total_ar * 100)}%` }}
              >
                {((report?.summary?.days_31_60 || 0) / report?.summary?.total_ar * 100) > 10 ? '31-60' : ''}
              </div>
              <div 
                className="bg-orange-500 flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${((report?.summary?.days_61_90 || 0) / report?.summary?.total_ar * 100)}%` }}
              >
                {((report?.summary?.days_61_90 || 0) / report?.summary?.total_ar * 100) > 10 ? '61-90' : ''}
              </div>
              <div 
                className="bg-red-500 flex items-center justify-center text-white text-xs font-medium"
                style={{ width: `${report?.summary?.percentage_over_90 || 0}%` }}
              >
                {report?.summary?.percentage_over_90 > 10 ? '90+' : ''}
              </div>
            </>
          )}
        </div>
        
        {/* Bucket Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          {Object.entries(buckets).map(([key, bucket]) => (
            <button
              key={key}
              onClick={() => setSelectedBucket(selectedBucket === key ? null : key)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedBucket === key
                  ? 'bg-teal-50 border-teal-300 ring-2 ring-teal-200'
                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
              }`}
            >
              <p className={`text-sm font-medium ${
                key === 'current' ? 'text-green-600' :
                key === 'days_31_60' ? 'text-yellow-600' :
                key === 'days_61_90' ? 'text-orange-600' :
                'text-red-600'
              }`}>
                {bucket.label}
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {formatCurrency(bucket.total)}
              </p>
              <p className="text-sm text-slate-500">
                {bucket.count} invoice{bucket.count !== 1 ? 's' : ''}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Details */}
      {selectedBucket && buckets[selectedBucket] && (
        <div className="card-clinical p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            {buckets[selectedBucket].label} - {buckets[selectedBucket].count} Invoices
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Invoice Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Days Old</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Original</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Balance</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Claim</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Plan</th>
                </tr>
              </thead>
              <tbody>
                {buckets[selectedBucket].invoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-slate-500">
                      No invoices in this aging bucket
                    </td>
                  </tr>
                ) : (
                  buckets[selectedBucket].invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{inv.patient_name}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {formatDate(inv.created_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${
                          inv.days_old > 90 ? 'text-red-600' :
                          inv.days_old > 60 ? 'text-orange-600' :
                          inv.days_old > 30 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {inv.days_old}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-slate-600">
                        {formatCurrency(inv.subtotal)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-slate-900">
                        {formatCurrency(inv.balance_due)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(inv.status)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {inv.has_claim ? (
                          <span className="text-green-500">✓</span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {inv.has_payment_plan ? (
                          <span className="text-green-500">✓</span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Collection Recommendations */}
      {report?.summary?.over_90 > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Warning size={24} className="text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Collection Action Recommended</p>
              <p className="text-sm text-red-700 mt-1">
                You have {formatCurrency(report?.summary?.over_90)} in accounts over 90 days old 
                ({report?.summary?.percentage_over_90}% of total A/R). Consider implementing 
                payment plans or collection procedures for these accounts.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
